import { apiService, ApiError } from './apiService';

export interface GsCountry {
  iso: string;
  name: string;
}

export interface GsProduct {
  id: number;
  name: string;
  description?: string | null;
  type: string;
  family: string;
  operatorId?: number | null;
  operatorName?: string | null;
  countryIso?: string | null;
  countryName?: string | null;
  requiredCreditPartyFields?: string[];
  validity?: unknown;
  benefits?: unknown;
  destination?: { amount?: number; min?: number; max?: number; unit?: string } | null;
  range?: { min?: number; max?: number; unit?: string } | null;
}

export interface GsQuote {
  productId: number;
  productName: string;
  family: string;
  operatorName?: string;
  countryIso?: string;
  countryName?: string;
  customerZar: number;
  customerZarCents: number;
  destination?: { amount?: number; unit?: string } | null;
  rangedAmount?: number | null;
  calculationMode?: string | null;
  requiredCreditPartyFields: string[];
  expiresAt: string;
  quote: Record<string, unknown>;
}

export interface GsPurchase {
  id: number;
  externalId: string;
  status: string;
  family: string;
  productName?: string | null;
  customerZar: number;
  fulfilment?: { pin?: string; iccid?: string; qr?: string } | null;
  lastErrorMessage?: string | null;
}

function unwrap<T>(res: { data?: unknown }): T {
  const body = res.data as { data?: T } | T;
  if (body && typeof body === 'object' && 'data' in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export const globalServicesService = {
  async availability(): Promise<{ enabled: boolean; families: string[] }> {
    const res = await apiService.request('/api/v1/global-services/availability');
    return unwrap(res);
  },

  async countries(): Promise<GsCountry[]> {
    const res = await apiService.request('/api/v1/global-services/countries');
    return unwrap(res) || [];
  },

  async families(countryIso: string): Promise<string[]> {
    const res = await apiService.request(
      `/api/v1/global-services/families?countryIso=${encodeURIComponent(countryIso)}`
    );
    return unwrap(res) || [];
  },

  async products(countryIso: string, family: string): Promise<GsProduct[]> {
    const res = await apiService.request(
      `/api/v1/global-services/products?countryIso=${encodeURIComponent(countryIso)}&family=${encodeURIComponent(family)}`
    );
    return unwrap(res) || [];
  },

  async lookup(mobileNumber: string) {
    const res = await apiService.request('/api/v1/global-services/lookup', {
      method: 'POST',
      body: JSON.stringify({ mobileNumber }),
    });
    return unwrap(res) as {
      mobileNumber: string;
      operatorId: number | null;
      operatorName: string | null;
      countryIso: string | null;
      confirmedRequired: boolean;
    };
  },

  async quote(body: { productId: number; rangedAmount?: number; calculationMode?: string }): Promise<GsQuote> {
    const res = await apiService.request('/api/v1/global-services/quote', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return unwrap(res);
  },

  async purchase(body: {
    productId: number;
    idempotencyKey: string;
    quote: Record<string, unknown>;
    creditParty: Record<string, string>;
    rangedAmount?: number;
    calculationMode?: string;
  }): Promise<GsPurchase> {
    const res = await apiService.request('/api/v1/global-services/purchase', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return unwrap(res);
  },

  async getPurchase(id: number): Promise<GsPurchase> {
    const res = await apiService.request(`/api/v1/global-services/purchases/${id}`);
    return unwrap(res);
  },
};

export { ApiError };
